/**
 * Modal Component - Backward Compatibility Wrapper
 *
 * Wraps shadcn Dialog to provide legacy Modal API:
 * - Controlled isOpen/onClose pattern
 * - ModalHeader/ModalBody/ModalFooter composition
 * - Size variants (sm, md, lg, xl, full)
 * - Stack index support for z-ordering
 *
 * This wrapper provides drop-in compatibility for legacy Modal usage
 * while using shadcn Dialog primitives under the hood.
 */

import * as React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog"
import { cn } from "../../utils"

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  header?: React.ReactNode
  hideHeader?: boolean
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
  style?: React.CSSProperties
  stackIndex?: number
}

// Size to max-width mapping
const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]',
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  header,
  hideHeader = false,
  children,
  size = 'lg',
  className = '',
  style,
  stackIndex = 0,
}) => {
  // Calculate z-index for modal stacking
  const zIndex = 50 + (stackIndex * 10)

  // Hide Dialog's built-in close button when custom header is provided (to avoid duplicates)
  const hideCloseButton = !!header

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          sizeClasses[size],
          'p-0 gap-0', // Remove default padding/gap - ModalBody/ModalFooter handle it
          className
        )}
        style={{ ...style, zIndex }}
        hideCloseButton={hideCloseButton}
      >
        {/* Header */}
        {!hideHeader && (
          header ? (
            header
          ) : title ? (
            <DialogHeader className="px-6 py-4">
              <DialogTitle className="text-lg font-semibold text-foreground">
                {title}
              </DialogTitle>
            </DialogHeader>
          ) : null
        )}

        {/* Content */}
        {children}
      </DialogContent>
    </Dialog>
  )
}

// Section components (unchanged API)
export const ModalHeader: React.FC<{
  title?: React.ReactNode
  right?: React.ReactNode
  onClose?: () => void // Note: Dialog handles close button automatically
  className?: string
}> = ({ title, right, className = '' }) => (
  <div className={cn("px-6 py-4", className)}>
    <div className="flex items-center justify-between">
      <div className="min-w-0">
        {typeof title === 'string' ? (
          <h3 className="text-lg font-semibold text-foreground truncate">{title}</h3>
        ) : title}
      </div>
      <div className="flex items-center gap-2">
        {right}
        {/* Note: Dialog already has close button, onClose is handled by Dialog */}
      </div>
    </div>
  </div>
)

export const ModalBody: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className = '', children }) => (
  <div className={className}>
    {children}
  </div>
)

export const ModalFooter: React.FC<{
  left?: React.ReactNode
  right?: React.ReactNode
  className?: string
}> = ({ left, right, className = '' }) => (
  <div className={cn("px-6 py-4 bg-secondary flex items-center justify-between gap-3", className)}>
    <div className="flex items-center gap-2">{left}</div>
    <div className="flex items-center gap-2">{right}</div>
  </div>
)
